import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { UserKeys, getGroups } from '../api/users-api';
import { useMemo, useState } from 'react';
import useDebounce from '../utils/useDebounce';
import GroupsBigQueryAutocomplete from './groups-bigquery-autocomplete';
import { useUserSettings } from '../utils/use-user-settings';
import { isFeatureEnabled } from '../utils/feature-flags';
import { emailRegex } from '../utils/regular-expressions';

import Autocomplete from '@mui/material/Autocomplete';
import Avatar from '@mui/material/Avatar';
import CircularProgress from '@mui/material/CircularProgress';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import TextField from '@mui/material/TextField';
import Group from '@mui/icons-material/Group';

interface GroupsAutocompleteProps {
  autocompleteValue: string;
  setAutoCompleteValue: (value: string) => void;
  setAutoCompleteTypeValue?: (value: string) => void;
  size?: 'small' | 'medium';
  disabled?: boolean;
  freeSolo?: boolean;
  newKey?: string;
  errors?: boolean;
  helperText?: string;
  onSelection?: (value: string) => void;
  label?: string;
}

export default function GroupsAutocomplete({
  size,
  disabled,
  freeSolo,
  autocompleteValue,
  setAutoCompleteValue,
  newKey,
  setAutoCompleteTypeValue,
  errors,
  helperText,
  onSelection,
  label,
}: GroupsAutocompleteProps) {
  const { currentTier, features } = useUserSettings();
  const [inputValue, setInputValue] = useState('');
  const debouncedInput = useDebounce(
    freeSolo ? autocompleteValue : inputValue,
    500
  );

  const groupsEnabled = debouncedInput !== '' && autocompleteValue === '';

  const freeSoloGroupEnabled = debouncedInput !== '';

  const { data: groupData, isFetching: groupsLoading } = useQuery({
    queryKey: [UserKeys.Groups, { debouncedInput }],
    queryFn: () =>
      getGroups(
        {
          cursor: null,
          results: [],
          nameSpace: '',
          firstPull: true,
          searchTime: 0,
          pageSize: 5,
        },
        debouncedInput
      ),
    enabled: freeSolo ? freeSoloGroupEnabled : groupsEnabled,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 1,
  });

  interface Option {
    email: string;
    type: string;
    firstName?: string;
    lastName?: string;
    numberOfMembers?: number;
  }

  const groupOptions: Option[] =
    groupData?.results.map((result) => {
      return {
        email: result.email,
        type: 'group',
        numberOfMembers: result.directMemberCount,
      };
    }) || [];
  const freeSoloOption: Option[] =
    freeSolo &&
    autocompleteValue &&
    emailRegex.test(autocompleteValue) &&
    !groupOptions.some((option) => option.email === autocompleteValue)
      ? [
          {
            email: autocompleteValue,
            type: 'group',
            numberOfMembers: 0,
          },
        ]
      : [];
  const options = [...groupOptions, ...freeSoloOption];

  function createNoOptionsText() {
    let text = 'No results found.';
    if (debouncedInput === '') {
      text = 'Start typing to search...';
    } else if (groupData?.results.length === 0) {
      text = 'No results found.';
    }

    return text;
  }

  const key = useMemo(
    () => newKey || new Date().getTime().toString(),
    [newKey]
  );

  return currentTier === 'enterprise' &&
    isFeatureEnabled('indexGroupsReact', features) ? (
    <GroupsBigQueryAutocomplete
      newKey={key}
      autocompleteValue={autocompleteValue}
      setAutoCompleteValue={setAutoCompleteValue}
      setAutoCompleteTypeValue={setAutoCompleteTypeValue}
      errors={errors}
      helperText={helperText}
      size={size}
      disabled={disabled}
      freeSolo={freeSolo}
      onSelection={onSelection}
      label={label}
    />
  ) : (
    <Autocomplete
      id="groups-autocomplete"
      key={key}
      disabled={disabled}
      filterOptions={(x) => x}
      options={options}
      size={size}
      componentsProps={{
        paper: {
          sx: {
            minWidth: 400,
          },
        },
      }}
      autoComplete
      autoHighlight
      freeSolo={freeSolo}
      includeInputInList
      filterSelectedOptions={!freeSolo}
      noOptionsText={createNoOptionsText()}
      getOptionLabel={(option) => {
        if (typeof option === 'string') {
          return (
            groupData?.results.find((group) => group.email === option)?.email ||
            ''
          );
        } else {
          return option.email;
        }
      }}
      isOptionEqualToValue={(option, value) => {
        if (typeof option === 'string') {
          return option === value;
        } else {
          return option.email === value?.email;
        }
      }}
      // value={autocompleteValue}
      onChange={(_event, newValue, reason) => {
        let selectedValue = '';
        if (reason === 'clear') {
          setAutoCompleteValue('');
          if (onSelection) {
            onSelection('');
          }
          return;
        }
        if (!freeSolo) {
          if (typeof newValue === 'string') {
            selectedValue = newValue;
            setAutoCompleteValue(newValue);
          } else {
            if (setAutoCompleteTypeValue) {
              setAutoCompleteTypeValue(newValue?.type || '');
            }
            selectedValue = newValue?.email || '';
            setAutoCompleteValue(selectedValue);
          }
        } else {
          if (typeof newValue === 'string') {
            selectedValue = newValue;
            setAutoCompleteValue(newValue);
          } else if (newValue !== null) {
            selectedValue = newValue.email || '';
            setAutoCompleteValue(selectedValue);
          }
        }

        if (
          selectedValue &&
          (reason === 'selectOption' || reason === 'createOption') &&
          onSelection
        ) {
          onSelection(selectedValue);
        }
      }}
      renderOption={(props, option: Option) => {
        return (
          <ListItem {...props} key={option.email} dense>
            <ListItemAvatar>
              <Avatar>
                <Group />
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              id={option.email}
              primary={option.email}
              secondary={`${option.numberOfMembers} members`}
            />
          </ListItem>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Search for a group"
          fullWidth
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {groupsLoading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          error={errors}
          helperText={helperText}
        />
      )}
      inputValue={freeSolo ? autocompleteValue : inputValue}
      onInputChange={(_event, newInputValue) => {
        if (freeSolo) {
          setAutoCompleteValue(newInputValue);
        } else {
          setInputValue(newInputValue);
        }
      }}
    />
  );
}
